import { useState } from "react";
import "./VisionTT.css";
import ModalBox from "../../components/ModalBox/ModalBox";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";
import ActivityHeader from "../../components/ActivityHeader/ActivityHeader";
import { initialModal, levelPassedModal, completionModal } from "./Modals";
import { trackGameStart } from "../../utils/trackEvents";

function VisionTT() {
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [modalContent, setModalContent] = useState(initialModal());
  const [level, setLevel] = useState(1);
  const [boxes, setBoxes] = useState([]);
  const [differentBoxIndex, setDifferentBoxIndex] = useState(null);
  const [showRetry, setShowRetry] = useState(false);

  const colors = [
    { normal: "#8ce167", different: "#7fca5e" },
    { normal: "#F5B041", different: "#F7C15A" },
    { normal: "#A569BD", different: "#B07CCF" },
    { normal: "#5DADE2", different: "#6FBDE9" },
    { normal: "#E74C3C", different: "#EA6153" },
  ];

  function startNewLevel() {
    const boxCount = 6;
    setBoxes(Array.from({ length: boxCount }, (_, i) => ({ id: i })));
    setDifferentBoxIndex(Math.floor(Math.random() * boxCount));
    trackGameStart("VisionTT", level);
  }

  function handleBoxClick(index) {
    if (showRetry) return;
    if (index === differentBoxIndex) {
      if (level < 5) {
        setModalContent(levelPassedModal(level));
        setLevel((prev) => prev + 1);
      } else {
        setModalContent(completionModal());
        setLevel(1);
      }
      setIsModalOpen(true);
    } else {
      setShowRetry(true);
    }
  }

  function handleModalClose() {
    setIsModalOpen(false);
    setShowRetry(false);
    setBoxes([]);
    setDifferentBoxIndex(null);
    startNewLevel();
  }

  function retryLevel() {
    setShowRetry(false);
    setBoxes([]);
    setDifferentBoxIndex(null);
    startNewLevel();
  }

  function Box({ realIndex }) {
    const color =
      realIndex === differentBoxIndex
        ? colors[level - 1].different
        : colors[level - 1].normal;

    let touchTimer = null;

    const handleTouchStart = (e) => {
      const { left, top, width, height } =
        e.currentTarget.getBoundingClientRect();
      const x = ((e.touches[0].clientX - left) / width) * 100 + "%";
      const y = ((e.touches[0].clientY - top) / height) * 100 + "%";
      e.currentTarget.style.setProperty("--x", x);
      e.currentTarget.style.setProperty("--y", y);
      e.currentTarget.classList.add("mobile-hover");

      touchTimer = setTimeout(() => {
        touchTimer = null;
      }, 200);
    };

    const handleTouchEnd = (e) => {
      e.currentTarget.classList.remove("mobile-hover");
      if (touchTimer) {
        clearTimeout(touchTimer);
        handleBoxClick(realIndex);
      }
    };

    return (
      <div
        className="visionTT-box"
        style={{
          "--box-bg": showRetry ? color : "white",
          "--hover-bg": color,
          "--x": "50%",
          "--y": "50%",
        }}
        onMouseMove={(e) => {
          const { left, top, width, height } =
            e.currentTarget.getBoundingClientRect();
          const x = ((e.clientX - left) / width) * 100 + "%";
          const y = ((e.clientY - top) / height) * 100 + "%";
          e.currentTarget.style.setProperty("--x", x);
          e.currentTarget.style.setProperty("--y", y);
        }}
        onClick={() => handleBoxClick(realIndex)}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {showRetry && realIndex === differentBoxIndex && (
          <div className="visionTT-label correct">✅</div>
        )}
      </div>
    );
  }

  return (
    <div className="visionTT-main">
      {!isModalOpen && (
        <>
          <ActivityHeader>Level {level}</ActivityHeader>
          <div className="visionTT-content-multi">
            <div className="visionTT-row">
              {boxes.slice(0, 3).map((box, idx) => (
                <Box key={box.id} realIndex={idx} />
              ))}
            </div>
            <div className="visionTT-row">
              {boxes.slice(3).map((box, idx) => (
                <Box key={box.id} realIndex={idx + 3} />
              ))}
            </div>
          </div>
          {showRetry && (
            <div className="visionTT-retry">
              <PrimaryButton onClick={retryLevel} type="retry">
                Retry
              </PrimaryButton>
            </div>
          )}
        </>
      )}

      <ModalBox
        isOpen={isModalOpen}
        title={modalContent.title}
        description={modalContent.description}
        buttonText={modalContent.buttonText}
        onClose={handleModalClose}
      />
    </div>
  );
}

export default VisionTT;
